import { useCallback, useState } from 'react';

/**
 * Der Schluessel des abgeloesten Modus-Speichers. Bewusst als Literal hier und
 * nicht als Import aus `viewModePreference` — das Modul ist mit Task B2
 * geloescht, sein Schluessel lebt aber in den Browsern der Nutzer weiter und
 * wird noch gelesen. Ein Import auf ein totes Modul waere der Grund, es doch
 * zu behalten.
 */
const LEGACY_VIEW_MODE_PREFIX = 'workbench-view-mode:';

/**
 * Breite der Produktleiste in der Werkbank.
 *
 * Ersetzt den fruehreren Modusumschalter (`card` / `list`). Die beiden Modi
 * waren zwei getrennte Flaechen mit zwei Datenpfaden; seit Task B1 gibt es nur
 * noch eine Flaeche, und was bleibt, ist eine Frage der BREITE:
 *
 *   `hidden` — 0 px Leiste, nur der Detailbereich. Exakt die heutige Karte.
 *   `normal` — 300 px Leiste neben dem Detailbereich. Das Neue.
 *   `wide`   — Leiste ueber die volle Breite, kein Detailbereich. Exakt die
 *              heutige Liste.
 *
 * Beide Enden bleiben also erreichbar: wer bisher Karte wollte, bekommt sie
 * (`hidden`), wer Liste wollte, auch (`wide`). Umgestiegen wird nicht, es
 * kommt nur eine Zwischenstufe dazu.
 */
export type RailWidth = 'hidden' | 'normal' | 'wide';

export const RAIL_WIDTHS: readonly RailWidth[] = ['hidden', 'normal', 'wide'] as const;

/** Praefix des localStorage-Schluessels. Der Feed bildet den Rest. */
export const RAIL_WIDTH_STORAGE_PREFIX = 'workbench-rail-width:';

export function railWidthStorageKey(feedId: string): string {
  return `${RAIL_WIDTH_STORAGE_PREFIX}${feedId}`;
}

function isRailWidth(value: unknown): value is RailWidth {
  return typeof value === 'string' && (RAIL_WIDTHS as readonly string[]).includes(value);
}

/**
 * Uebersetzung der Altwerte aus `viewModePreference`.
 *
 * Ein Modus war eine Aussage ueber die ganze Flaeche, eine Breite ist eine
 * ueber die Leiste — die beiden Enden entsprechen sich aber genau, und darauf
 * beruht die Migration. Ein unbekannter Altwert fuehrt bewusst NICHT in eine
 * vierte Breite, sondern auf die Voreinstellung.
 */
const LEGACY_VIEW_MODE: Record<string, RailWidth> = {
  card: 'hidden',
  list: 'wide',
};

/**
 * Liest die gemerkte Leistenbreite eines Feeds.
 *
 * Reihenfolge: neuer Wert vor Altwert. Andersherum waere die Migration eine
 * Fessel — die alte Wahl ueberstimmte jede neue, und der Umschalter waere
 * nach einem Neuladen wirkungslos.
 *
 * Faellt auf `'normal'` zurueck, wenn nichts gespeichert ist, der Wert keine
 * gueltige Breite ist oder der Speicher gar nicht lesbar ist. Letzteres ist
 * kein Randfall: im Safari-Privatmodus und bei voller Quote wirft
 * `localStorage`, und eine Ansichtswahl darf niemals die Seite reissen.
 */
export function readRailWidth(feedId: string | null): RailWidth {
  if (!feedId) return 'normal';
  try {
    const stored = window.localStorage.getItem(railWidthStorageKey(feedId));
    if (isRailWidth(stored)) return stored;

    const legacy = window.localStorage.getItem(`${LEGACY_VIEW_MODE_PREFIX}${feedId}`);
    if (legacy && legacy in LEGACY_VIEW_MODE) return LEGACY_VIEW_MODE[legacy];

    return 'normal';
  } catch {
    return 'normal';
  }
}

/** Gegenstueck zu `readRailWidth`. Schluckt Speicherfehler bewusst. */
export function writeRailWidth(feedId: string | null, width: RailWidth): void {
  if (!feedId) return;
  try {
    window.localStorage.setItem(railWidthStorageKey(feedId), width);
  } catch {
    // Nicht speichern zu koennen ist kein Fehler, der den Nutzer erreichen muss.
  }
}

/**
 * Leistenbreite mit Gedaechtnis pro Feed.
 *
 * Der Feedwechsel wird WAEHREND des Renders nachgezogen, nicht per Effect —
 * dasselbe Muster wie `useStatusPreference` und der Puffer-Reset in
 * `useWorkbench.ts`. Ein Effect liesse einen Frame zu, in dem `feedId` schon
 * der neue Feed ist, die Breite aber noch die des alten; die Werkbank wuerde
 * in diesem Frame das falsche Layout mounten und es sofort wieder wegwerfen
 * (inklusive Virtualizer der Leiste).
 */
export function useRailWidthPreference(
  feedId: string | null,
): [RailWidth, (width: RailWidth) => void] {
  const [state, setState] = useState<{ feedId: string | null; width: RailWidth }>(() => ({
    feedId,
    width: readRailWidth(feedId),
  }));

  if (state.feedId !== feedId) {
    setState({ feedId, width: readRailWidth(feedId) });
  }

  const setWidth = useCallback(
    (width: RailWidth) => {
      setState({ feedId, width });
      writeRailWidth(feedId, width);
    },
    [feedId],
  );

  return [state.width, setWidth];
}
